import React from 'react';
import { ChartSettings as ChartSettingsType, ChartDisplayMode, YAxisScale } from '../types';

interface ChartSettingsProps {
  settings: ChartSettingsType;
  onSettingsChange: (settings: Partial<ChartSettingsType>) => void;
  visible: boolean;
  onClose: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'rgba(33, 40, 44, 0.98)',
    borderRadius: '12px',
    border: '1px solid rgba(108, 117, 125, 0.3)',
    width: '400px',
    maxWidth: '90vw',
    maxHeight: '80vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.25rem',
    borderBottom: '1px solid rgba(108, 117, 125, 0.2)',
  },
  title: {
    color: '#c5c5c5',
    fontSize: '1rem',
    fontWeight: 500,
    margin: 0,
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: '#8b949e',
    cursor: 'pointer',
    padding: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: '1.25rem',
    overflowY: 'auto' as const,
    maxHeight: 'calc(80vh - 120px)',
  },
  section: {
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    color: '#8b949e',
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.75rem',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    color: '#c5c5c5',
    fontSize: '0.85rem',
    marginBottom: '0.4rem',
  },
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: '6px',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'rgba(33, 40, 44, 0.95)',
    color: '#c5c5c5',
    fontSize: '0.85rem',
  },
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: '6px',
    border: '1px solid rgba(108, 117, 125, 0.4)',
    background: 'rgba(33, 40, 44, 0.95)',
    color: '#c5c5c5',
    fontSize: '0.85rem',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
  },
  checkboxInput: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    accentColor: '#53bde4',
  },
  range: {
    width: '100%',
    height: '4px',
    borderRadius: '2px',
    background: 'rgba(108, 117, 125, 0.3)',
    appearance: 'none' as const,
    cursor: 'pointer',
  },
  rangeValue: {
    color: '#53bde4',
    fontSize: '0.8rem',
    marginLeft: '0.5rem',
  },
  row: {
    display: 'flex',
    gap: '1rem',
  },
  halfWidth: {
    flex: 1,
  },
};

const DISPLAY_MODE_OPTIONS: { value: ChartDisplayMode; label: string; description: string }[] = [
  { value: 'unified', label: 'Unified', description: 'All metrics on one Y-axis' },
  { value: 'split', label: 'Split Y-Axes', description: 'Each metric gets its own Y-axis' },
  { value: 'stacked', label: 'Stacked', description: 'Stacked area chart' },
  { value: 'normalized', label: 'Normalized', description: 'All metrics scaled to 0-100%' },
];

const Y_AXIS_SCALE_OPTIONS: { value: YAxisScale; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'manual', label: 'Manual' },
  { value: 'log', label: 'Logarithmic' },
];

export function ChartSettingsModal({
  settings,
  onSettingsChange,
  visible,
  onClose,
}: ChartSettingsProps) {
  if (!visible) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.title}>Chart Settings</h3>
          <button style={styles.closeButton} onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Display Mode */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Display Mode</div>
            <div style={styles.formGroup}>
              <select
                value={settings.displayMode}
                onChange={e => onSettingsChange({ displayMode: e.target.value as ChartDisplayMode })}
                style={styles.select}
              >
                {DISPLAY_MODE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} - {opt.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Y-Axis Settings */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Y-Axis</div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Scale</label>
              <select
                value={settings.yAxisScale}
                onChange={e => onSettingsChange({ yAxisScale: e.target.value as YAxisScale })}
                style={styles.select}
              >
                {Y_AXIS_SCALE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {settings.yAxisScale === 'manual' && (
              <div style={styles.row}>
                <div style={styles.halfWidth}>
                  <label style={styles.label}>Min</label>
                  <input
                    type="number"
                    value={settings.yAxisMin ?? ''}
                    onChange={e => onSettingsChange({ yAxisMin: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="Auto"
                    style={styles.input}
                  />
                </div>
                <div style={styles.halfWidth}>
                  <label style={styles.label}>Max</label>
                  <input
                    type="number"
                    value={settings.yAxisMax ?? ''}
                    onChange={e => onSettingsChange({ yAxisMax: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="Auto"
                    style={styles.input}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Line Style */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Line Style</div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Line Width: <span style={styles.rangeValue}>{settings.lineWidth}px</span>
              </label>
              <input
                type="range"
                min="1"
                max="5"
                step="0.5"
                value={settings.lineWidth}
                onChange={e => onSettingsChange({ lineWidth: Number(e.target.value) })}
                style={styles.range}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={settings.showPoints}
                  onChange={e => onSettingsChange({ showPoints: e.target.checked })}
                  style={styles.checkboxInput}
                />
                Show data points
              </label>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={settings.fillArea}
                  onChange={e => onSettingsChange({ fillArea: e.target.checked })}
                  style={styles.checkboxInput}
                />
                Fill area under lines
              </label>
            </div>
          </div>

          {/* Grid & Extras */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Display Options</div>

            <div style={styles.formGroup}>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={settings.showGrid}
                  onChange={e => onSettingsChange({ showGrid: e.target.checked })}
                  style={styles.checkboxInput}
                />
                Show grid lines
              </label>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={settings.showRangeslider}
                  onChange={e => onSettingsChange({ showRangeslider: e.target.checked })}
                  style={styles.checkboxInput}
                />
                Show range slider (overview)
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChartSettingsModal;
